/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * One photo attached to a circle message.
 *
 * The endpoint is behind a bearer token, so this cannot be a plain <img src>.
 * The bytes are fetched, turned into an object URL, and the URL is revoked when
 * the component unmounts — without that, scrolling a busy circle leaks a blob
 * per thumbnail for the lifetime of the tab.
 */

import { useEffect, useState } from "react";

import { CirclesApi } from "../../api/endpoints.ts";
import { usePrefs } from "../../i18n/index.tsx";

/**
 * `aspect` controls how the photo sits in a post.
 *
 * It used to be a fixed `h-24 w-24` — a 96px thumbnail, which rendered
 * someone's dinner smaller than the timestamp printed beside it. In a feature
 * whose entire purpose is "here's what I actually cooked", the picture is the
 * content, so a single-photo post now runs edge to edge at 4:3 and multi-photo
 * posts tile as squares.
 *
 *   wide    — one photo, 4:3, full width of the card
 *   square  — one of several, tiled
 *   thumb   — the old size, kept for the composer's pending-photo strip
 */
type Aspect = "wide" | "square" | "thumb";

const ASPECT: Record<Aspect, string> = {
  wide: "aspect-[4/3] w-full",
  square: "aspect-square w-full",
  thumb: "h-24 w-24",
};

export function CirclePhoto({ circleId, messageId, photoId, onOpen, aspect = "thumb" }: {
  circleId: string;
  messageId: string;
  photoId: string;
  onOpen?: (url: string) => void;
  aspect?: Aspect;
}) {
  const { t } = usePrefs();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;

    CirclesApi.photoUrl(circleId, messageId, photoId)
      .then((u) => {
        // The effect may have been torn down while the fetch was in flight.
        // Revoking immediately keeps that case from leaking.
        if (revoked) { URL.revokeObjectURL(u); return; }
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => setFailed(true));

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [circleId, messageId, photoId]);

  const box = ASPECT[aspect];
  const rounded = aspect === "thumb" ? "rounded-lg border border-rule-soft" : "";

  if (failed) {
    return (
      <div className={`grid place-items-center bg-paper-3 text-xs text-ink-3 ${box} ${rounded}`}>
        {t("circles.photoFailed")}
      </div>
    );
  }

  if (!url) {
    return <div className={`animate-pulse bg-paper-3 ${box} ${rounded}`} />;
  }

  return (
    <button
      type="button"
      className={`block overflow-hidden bg-paper-3 ${box} ${rounded}`}
      onClick={() => onOpen?.(url)}
      aria-label={t("circles.viewPhoto")}
    >
      <img src={url} alt="" loading="lazy" className="h-full w-full object-cover" />
    </button>
  );
}
